import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Paperclip, X, Loader2 } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { SEO_META, waUrl, INDUSTRIES } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Contact", "Requirement", "Customization", "Project", "Files"];
const CUSTOMIZATION_CHOICES = ["Embroidery", "Screen Printing", "Heat Transfer", "Custom Labels", "Custom Packaging", "Reflective Details", "Other"];
const EMPTY = {
  full_name: "", company: "", email: "", phone: "", country: "",
  industry: "", product_type: "", quantity: "", colors: "", size_range: "",
  customization: [], delivery_date: "", destination: "", message: "",
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="field-label">{label}{required && <span className="text-cleef"> *</span>}</span>
    {children}
  </label>
);

export default function BulkQuote() {
  const meta = SEO_META.quote;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);
  const startedRef = useRef(false);
  const fileInput = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const markStarted = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "quote_form_start" });
    }
  };
  const toggleCustom = (c) =>
    setForm((f) => ({ ...f, customization: f.customization.includes(c) ? f.customization.filter((x) => x !== c) : [...f.customization, c] }));

  const addFiles = (list) => {
    const next = [...files, ...Array.from(list)].slice(0, 6);
    setFiles(next);
  };

  const validStep = () => {
    if (step === 0) return form.full_name.trim() && /.+@.+\..+/.test(form.email) && form.country.trim();
    if (step === 1) return form.industry && form.product_type.trim() && form.quantity.trim();
    return true;
  };

  const submit = async () => {
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, Array.isArray(v) ? v.join(",") : v));
      files.forEach((f) => fd.append("files", f));
      const res = await axios.post(`${API}/leads/quote`, fd);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "quote_form_submit", reference: res.data.reference });
      setReference(res.data.reference);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error("Submission failed. Please try again or reach us on WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  if (reference) {
    return (
      <>
        <SEO title={meta.title} description={meta.description} path={meta.path} />
        <section className="container-site py-20 lg:py-28" data-testid="quote-success">
          <div className="mx-auto max-w-xl border border-obsidian/10 bg-white p-10 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center bg-cleef-light">
              <Check className="h-7 w-7 text-cleef" />
            </span>
            <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight">Thank you.</h1>
            <p className="mt-3 text-base leading-relaxed text-obsidian/60">
              Your requirement has been received. Our team will review the details and contact you.
            </p>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.18em] text-obsidian/45">
              Reference <span className="text-cleef" data-testid="quote-reference">{reference}</span>
            </p>
            <div className="mt-8 flex flex-col gap-3">
              <a
                href={waUrl(`Hello Cleef Empire, I just submitted a bulk quotation request. Reference: ${reference}. I would like to continue the discussion.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-event="whatsapp_click"
                data-testid="quote-success-whatsapp-btn"
                className="btn-dark !bg-[#128C7E]"
              >
                <WhatsAppIcon className="h-4 w-4" /> Continue on WhatsApp
              </a>
              <Link to="/uniforms" className="btn-outline" data-testid="quote-success-explore-btn">Explore Uniforms</Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO title={meta.title} description={meta.description} path={meta.path} />
      <section className="border-b border-obsidian/10 bg-bone" data-testid="quote-hero">
        <div className="container-site py-14 lg:py-20">
          <Reveal>
            <p className="eyebrow">Bulk Quotation</p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-black leading-[1.06] tracking-tight sm:text-5xl">
              Tell us what you need. We’ll help you build it.
            </h1>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-obsidian/60">
              Send your uniform requirement and our team can review the product, quantity, customization and
              delivery details.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-site py-14 lg:py-20" data-testid="quote-form-section">
        <div className="mx-auto max-w-2xl">
          <div className="mb-10" data-testid="quote-progress">
            <div className="flex items-center justify-between">
              {STEPS.map((s, i) => (
                <div key={s} className="flex flex-1 items-center last:flex-none">
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center border font-mono text-xs font-semibold transition-colors duration-300 ${i <= step ? "border-cleef bg-cleef text-white" : "border-obsidian/20 bg-white text-obsidian/40"}`}>
                    {i + 1}
                  </div>
                  {i < STEPS.length - 1 && <div className={`h-px flex-1 transition-colors duration-300 ${i < step ? "bg-cleef" : "bg-obsidian/15"}`} />}
                </div>
              ))}
            </div>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-obsidian/50">
              Step {step + 1} of {STEPS.length} — {STEPS[step]}
            </p>
          </div>

          <div className="border border-obsidian/10 bg-white p-7 sm:p-10" data-testid={`rfq-step-${step + 1}`} onFocus={markStarted}>
            {step === 0 && (
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Full Name" required><input className="field-input" value={form.full_name} onChange={set("full_name")} data-testid="quote-input-name" autoComplete="name" /></Field>
                <Field label="Company Name"><input className="field-input" value={form.company} onChange={set("company")} data-testid="quote-input-company" autoComplete="organization" /></Field>
                <Field label="Business Email" required><input type="email" className="field-input" value={form.email} onChange={set("email")} data-testid="quote-input-email" autoComplete="email" /></Field>
                <Field label="Phone / WhatsApp"><input type="tel" className="field-input" value={form.phone} onChange={set("phone")} data-testid="quote-input-phone" autoComplete="tel" /></Field>
                <Field label="Country" required><input className="field-input sm:col-span-2" value={form.country} onChange={set("country")} data-testid="quote-input-country" autoComplete="country-name" /></Field>
              </div>
            )}

            {step === 1 && (
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Industry" required>
                  <select className="field-input" value={form.industry} onChange={set("industry")} data-testid="quote-select-industry">
                    <option value="">Select industry</option>
                    {INDUSTRIES.map((i) => <option key={i.slug} value={i.name}>{i.name}</option>)}
                    <option value="Other">Other</option>
                  </select>
                </Field>
                <Field label="Product / Uniform Type" required><input className="field-input" placeholder="e.g. Scrubs, Chef Coats, Polos" value={form.product_type} onChange={set("product_type")} data-testid="quote-input-product" /></Field>
                <Field label="Estimated Quantity" required><input className="field-input" placeholder="e.g. 500 pieces" value={form.quantity} onChange={set("quantity")} data-testid="quote-input-quantity" /></Field>
                <Field label="Required Colors"><input className="field-input" placeholder="e.g. Navy, White" value={form.colors} onChange={set("colors")} data-testid="quote-input-colors" /></Field>
                <Field label="Size Range"><input className="field-input sm:col-span-2" placeholder="e.g. XS – 4XL" value={form.size_range} onChange={set("size_range")} data-testid="quote-input-sizes" /></Field>
              </div>
            )}

            {step === 2 && (
              <div>
                <p className="mb-5 text-sm text-obsidian/60">Select all that apply.</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {CUSTOMIZATION_CHOICES.map((c) => {
                    const active = form.customization.includes(c);
                    return (
                      <button
                        type="button"
                        key={c}
                        onClick={() => toggleCustom(c)}
                        data-testid={`quote-custom-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`flex items-center gap-3 border px-4 py-3.5 text-left text-sm font-medium transition-colors duration-200 ${active ? "border-cleef bg-cleef-light text-obsidian" : "border-obsidian/15 bg-white text-obsidian/70 hover:border-obsidian/35"}`}
                      >
                        <span className={`flex h-5 w-5 items-center justify-center border ${active ? "border-cleef bg-cleef" : "border-obsidian/25"}`}>
                          {active && <Check className="h-3 w-3 text-white" />}
                        </span>
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="grid gap-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Target Delivery Date"><input type="date" className="field-input" value={form.delivery_date} onChange={set("delivery_date")} data-testid="quote-input-date" /></Field>
                  <Field label="Destination Country / Port"><input className="field-input" value={form.destination} onChange={set("destination")} data-testid="quote-input-destination" /></Field>
                </div>
                <Field label="Message / Specifications">
                  <textarea rows={5} className="field-input resize-none" placeholder="Fabric preferences, branding placement, reference links or any other detail." value={form.message} onChange={set("message")} data-testid="quote-input-message" />
                </Field>
              </div>
            )}

            {step === 4 && (
              <div>
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  data-testid="quote-file-dropzone"
                  className="flex w-full flex-col items-center justify-center gap-3 border border-dashed border-obsidian/25 bg-ivory px-6 py-12 text-center transition-colors duration-200 hover:border-cleef"
                >
                  <Paperclip className="h-6 w-6 text-cleef" />
                  <span className="text-sm font-medium text-obsidian/75">Attach tech pack, reference image, logo, specification sheet or uniform photo</span>
                  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-obsidian/40">JPG · PNG · PDF · DOC · DOCX — up to 6 files</span>
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                  className="hidden"
                  data-testid="quote-file-input"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                />
                {files.length > 0 && (
                  <ul className="mt-4 space-y-2" data-testid="quote-file-list">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between border border-obsidian/10 bg-ivory px-4 py-2.5 text-sm">
                        <span className="truncate text-obsidian/75">{f.name}</span>
                        <button type="button" onClick={() => setFiles(files.filter((_, x) => x !== i))} aria-label={`Remove ${f.name}`} className="ml-3 text-obsidian/40 hover:text-cleef">
                          <X className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="mt-9 flex items-center justify-between gap-3 border-t border-obsidian/10 pt-6">
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                disabled={step === 0}
                data-testid="quote-back-btn"
                className="btn-outline disabled:pointer-events-none disabled:opacity-30"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <button type="button" onClick={() => validStep() && setStep((s) => s + 1)} disabled={!validStep()} data-testid="quote-next-btn" className="btn-primary disabled:pointer-events-none disabled:opacity-40">
                  Continue <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button type="button" onClick={submit} disabled={sending} data-event="quote_form_submit" data-testid="rfq-submit-button" className="btn-primary disabled:opacity-60">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Request Bulk Quotation
                </button>
              )}
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-obsidian/45">
            Prefer to talk first?{" "}
            <a href={waUrl("Hello Cleef Empire, I would like to request a bulk uniform quotation.")} target="_blank" rel="noopener noreferrer" data-event="whatsapp_click" className="font-semibold text-[#128C7E] underline underline-offset-4" data-testid="quote-alt-whatsapp-link">
              Continue on WhatsApp
            </a>
          </p>
        </div>
      </section>
    </>
  );
}
